import { removeHtmlTag } from '@exam-management/common/utils';
import { QBankStructureLevelCategory } from '@exam-management/graphql';
import type { QBankStructureLevel } from '@exam-management/qbs-provider';
import {
  NullishSearchSelectionSchema,
  validateWithoutPTag,
  getLocationSchema,
} from '@exam-management/qcontent-management';
import { z } from 'zod';

export const SingleQuestionCreationSchema = z.object({
  information: z.object({
    calculated: z.string().min(1, {
      message: 'กรุณาเลือกประเภทข้อสอบ',
    }),
    pValue: z.object({
      selected: z
        .enum(['pInitial'], {
          message: 'กรุณาเลือก P Default',
        })
        .default('pInitial'),
      pInitial: z
        .number({ message: 'กรุณาระบุค่า P' })
        .min(0, {
          message: 'เกินค่าต่ำสุด (0)',
        })
        .max(1, {
          message: 'เกินค่าสูงสุด (1)',
        }),
    }),
  }),
  detail: z.object({
    details: z.string().refine(
      (value) => {
        const details = removeHtmlTag(value, {
          customTag: 'p',
        }).trim();
        return details.length >= 1;
      },
      {
        message: 'กรุณากรอกคำถาม',
      },
    ),
    choices: z
      .array(
        z.object({
          value: z.string(),
          label: z
            .string()
            .trim()
            .refine(
              (value) => {
                const label = removeHtmlTag(value, {
                  customTag: 'p',
                }).trim();
                return label.length >= 1;
              },
              {
                message: 'กรุณากรอกตัวเลือก',
              },
            ),
          order: z.number(),
        }),
      )
      .min(2, 'กรุณากรอกตัวเลือกอย่างน้อย 2 ข้อ'),
    correctAnswer: z.object({
      value: z
        .string({
          message: 'กรุณาเลือกคำตอบที่ถูกต้อง',
        })
        .min(1, 'กรุณาเลือกคำตอบที่ถูกต้อง'),
    }),
  }),
  properties: z.object({
    cognitiveDomain: z.array(NullishSearchSelectionSchema).optional(),
    learningObjectiveStatement: NullishSearchSelectionSchema.refine(
      (data) => {
        return !!data?.value;
      },
      {
        message: 'กรุณาเลือกวัตถุประสงค์การเรียนรู้ (Learning Objective Statement (LOS))',
      },
    ),
    solution: validateWithoutPTag('กรุณากรอกคำอธิบายเฉลย'),
    reference: validateWithoutPTag('กรุณากรอกหนังสืออ้างอิง'),
  }),
});

export function getSingleQuestionSchema(qbsLevels: QBankStructureLevel[]) {
  return SingleQuestionCreationSchema.merge(
    z.object({
      location: z.object({
        [QBankStructureLevelCategory.Part]: getLocationSchema(
          QBankStructureLevelCategory.Part,
          qbsLevels,
        ),
        [QBankStructureLevelCategory.Subject]: getLocationSchema(
          QBankStructureLevelCategory.Subject,
          qbsLevels,
        ),
        [QBankStructureLevelCategory.Group]: getLocationSchema(
          QBankStructureLevelCategory.Group,
          qbsLevels,
        ),
        [QBankStructureLevelCategory.Chapter]: getLocationSchema(
          QBankStructureLevelCategory.Chapter,
          qbsLevels,
        ),
        [QBankStructureLevelCategory.Topic]: getLocationSchema(
          QBankStructureLevelCategory.Topic,
          qbsLevels,
        ),
        [QBankStructureLevelCategory.SubTopic]: getLocationSchema(
          QBankStructureLevelCategory.SubTopic,
          qbsLevels,
        ),
      }),
    }),
  );
}

export type SingleQuestionCreationFormInput = z.infer<
  typeof SingleQuestionCreationSchema & ReturnType<typeof getSingleQuestionSchema>
>;
